import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { CommonDrawer } from '@/components';
import { useToken } from '@/common/helper';
import {
  ExportUserActivity,
  GetUserActivity,
  type ActivityEventDTO,
} from '@/apis/activity';
import { saveBlobAsFile } from '@/pages/Shipments/components/fileDownload';
import {
  eventsChipLabel,
  getAllLeafEventTypeIds,
  groupByDay,
  timeWindowChipLabel,
} from '../utils/filterEvents/filterEvents';
import { useActivityUsersFilter } from '../hooks';
import useFetchActivityEventTypes from '../hooks/useFetchActivityEventTypes/useFetchActivityEventTypes';
import {
  setActivityFeedCache,
  takeActivityFeedCache,
} from '../activityFeedCache/activityFeedCache';
import type {
  ActivityEvent,
  ActivityEventTypeId,
  ActivityFeedInitialUser,
  ActivityFeedRestoreState,
} from '../types';
import { TimeWindowValue, TimezoneMode } from '../types';
import {
  ActivityPanelHeader,
  ActivityTimeline,
  EventsFilterDropdown,
  FilterChip,
  TimeWindowFilterDropdown,
  UsersFilterDropdown,
} from '../components';

interface ActivityFeedPanelProps {
  open: boolean;
  onClose: () => void;
  initialUser?: ActivityFeedInitialUser | null;
  // Fires for Alert/Comment/Notification rows — only those link to a shipment.
  onEventClick?: (
    event: ActivityEvent,
    snapshot: ActivityFeedRestoreState
  ) => void;
  // Turn off while a shipment drawer sits on top, so typing works there too.
  focusTrap?: boolean;
  restoreState?: ActivityFeedRestoreState | null;
}

// PRD: "infinite scroll loads older pages (50 to 100 events per fetch)".
const ACTIVITIES_PAGE_SIZE = 50;

const toActivityEvent = (row: ActivityEventDTO): ActivityEvent => ({
  id: row.id,
  userId: row.user_id ?? '',
  userName: row.user_name,
  typeId: row.type_id as ActivityEventTypeId,
  action: row.sentence ?? '',
  detail: row.detail,
  date: row.date,
  time: row.time,
  shipment_id: row.shipment_id,
  coc_id: row.coc_id,
  leg_type: row?.leg_type ?? null,
  execution_leg_id: row?.execution_leg_id ?? null,
  notification_source: row?.notification_source ?? null,
});

const ActivityFeedPanel: React.FC<ActivityFeedPanelProps> = ({
  open,
  onClose,
  initialUser,
  onEventClick,
  focusTrap = true,
  restoreState, //reopens filters/scroll/pagination back from shipmentDetailDrawer
}) => {
  const { token } = useToken();
  // A restored single-user narrow takes over from whatever opened the panel
  // (header vs. row) — it reflects where the user actually was when they clicked away.
  const effectiveInitialUser = restoreState
    ? restoreState.singleUser
    : initialUser;
  const usersFilter = useActivityUsersFilter(
    effectiveInitialUser,
    restoreState?.selection ?? null
  );

  // Events catalog comes from the backend now, fetched once per open — a
  // new event type just shows up here, no frontend deploy needed.
  const { eventTypeGroups, allEventTypeGroups, fetchEventTypes } =
    useFetchActivityEventTypes();
  const allLeafEventTypeIds = useMemo(
    () => getAllLeafEventTypeIds(allEventTypeGroups),
    [allEventTypeGroups]
  );
  // Restore already knows its selection; a fresh open has to wait for the
  // catalog, else the first fetch goes out with an empty filter.
  const eventTypesReady =
    Boolean(restoreState) || allEventTypeGroups.length > 0;
  const didFetchEventTypesRef = useRef(false);
  useEffect(() => {
    if (!open || didFetchEventTypesRef.current) return;
    didFetchEventTypesRef.current = true;
    fetchEventTypes().catch(console.error);
  }, [open, fetchEventTypes]);

  // null = "every event type". Can't pre-fill the full set before the
  // catalog loads, so `selectedEventTypeIds` below derives it live instead.
  const [customSelectedEventTypeIds, setCustomSelectedEventTypeIds] =
    useState<Set<string> | null>(() =>
      restoreState ? new Set(restoreState.selectedEventTypeIds) : null
    );
  const selectedEventTypeIds = useMemo(
    () => customSelectedEventTypeIds ?? new Set(allLeafEventTypeIds),
    [customSelectedEventTypeIds, allLeafEventTypeIds]
  );
  const eventTypeSelection = useMemo(
    () =>
      customSelectedEventTypeIds === null
        ? { select_all: true, include_ids: [] }
        : { select_all: false, include_ids: [...customSelectedEventTypeIds] },
    [customSelectedEventTypeIds]
  );

  const handleEventTypesChange = (next: Set<string>) => {
    setCustomSelectedEventTypeIds(
      next.size === allLeafEventTypeIds.length ? null : next
    );
  };

  // Debounced the same way the Users chip's search is — fetchEventTypes
  // would otherwise fire on every keystroke.
  const eventsSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    return () => {
      if (eventsSearchDebounceRef.current) {
        clearTimeout(eventsSearchDebounceRef.current);
      }
    };
  }, []);
  const handleEventsSearchChange = (value: string) => {
    if (eventsSearchDebounceRef.current) {
      clearTimeout(eventsSearchDebounceRef.current);
    }
    eventsSearchDebounceRef.current = setTimeout(() => {
      fetchEventTypes(value).catch(console.error);
    }, 500);
  };

  const [timeWindow, setTimeWindow] = useState<TimeWindowValue>(
    restoreState?.timeWindow ?? { preset: '7d' }
  );
  const [tzMode, setTzMode] = useState<TimezoneMode>(
    restoreState?.tzMode ?? 'browser'
  );

  // Reset everything on toggle activity drawer.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      usersFilter.resetSearchAndPaging();
      usersFilter.resetSelection(effectiveInitialUser, restoreState?.selection);
      setCustomSelectedEventTypeIds(
        restoreState ? new Set(restoreState.selectedEventTypeIds) : null
      );
      setTimeWindow(restoreState?.timeWindow ?? { preset: '7d' });
      setTzMode(restoreState?.tzMode ?? 'browser');
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, effectiveInitialUser]);

  // Backend requires select_all=false + exactly one user_id for "subject" —
  // singleUserMode alone can also be true via select-all-minus-everyone-else,
  const isExplicitSingleUser =
    usersFilter.selectionPayload.selectAll === false &&
    usersFilter.singleUserMode;
  const effectiveTzMode: TimezoneMode =
    tzMode === 'subject' && !isExplicitSingleUser ? 'browser' : tzMode;

  // --- activity list: fetch, pagination, restore-from-cache, scroll ---

  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [hasMoreActivities, setHasMoreActivities] = useState(false);
  // initial loading when panel opens
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Fetch activities,
  // Both the initial load and load-more go through here,
  const activitiesFetchIdRef = useRef(0);
  const isValidUserSelection = (selection: {
    selectAll?: boolean;
    userIds?: string[];
  }) => selection.selectAll === true || (selection.userIds?.length ?? 0) > 0;
  const isValidEventTypeSelection = (selection: {
    select_all?: boolean;
    include_ids?: string[];
  }) =>
    selection.select_all === true || (selection.include_ids?.length ?? 0) > 0;

  const fetchActivity = async (page: number) => {
    const fetchId = ++activitiesFetchIdRef.current;
    let fetched: ActivityEvent[];
    let hasMore: boolean;

    if (
      !isValidUserSelection(usersFilter.selectionPayload) ||
      !isValidEventTypeSelection(eventTypeSelection)
    ) {
      message.error('Select at least one user and one event type.');
      setActivitiesPage(page);
      setHasMoreActivities(false);
      return;
    }

    setActivitiesLoading(true);
    try {
      const res = await GetUserActivity(token, {
        timezone: effectiveTzMode,
        single_mode: usersFilter.singleUserMode,
        users: usersFilter.selectionPayload,
        event_type: eventTypeSelection,
        time_window: timeWindow,
        page,
        page_size: ACTIVITIES_PAGE_SIZE,
      });
      const data = res?.data?.data;
      if (!data?.is_success)
        throw new Error(data?.message || 'Activity log API not available yet');
      // Backend returns day-grouped buckets — flatten back to a flat list,
      // groupByDay() below re-groups and merges same-date runs across pages.
      const buckets = data.data ?? [];
      fetched = buckets.flatMap((bucket) =>
        bucket.activity.map(toActivityEvent)
      );
      hasMore = page < (data.page_count ?? 1);
    } catch (error) {
      const err = error as { data?: { data?: { message?: string } } };
      console.error(err?.data?.data?.message || 'Failed to load activity');
      fetched = [];
      hasMore = false;
    }

    if (fetchId !== activitiesFetchIdRef.current) return; // a newer fetch already landed
    setActivities((prev) => (page === 1 ? fetched : [...prev, ...fetched]));
    setActivitiesPage(page);
    setHasMoreActivities(hasMore);
    setActivitiesLoading(false);
  };

  // Opening the panel checks the activity cache first (see activityFeedCache.ts)
  // — a restore hit skips the network call, a miss just fetches page 1.
  const didInitialLoadRef = useRef(false);
  const [restorePagesLoaded, setRestorePagesLoaded] = useState(false);
  useEffect(() => {
    if (!open || !eventTypesReady) return;
    const isRestoring = !didInitialLoadRef.current && !!restoreState;
    didInitialLoadRef.current = true;

    const cached = isRestoring ? takeActivityFeedCache() : null;
    if (cached) {
      setActivities(cached.activities);
      setActivitiesPage(cached.activitiesPage);
      setHasMoreActivities(cached.hasMoreActivities);
      setActivitiesLoading(false);
      setRestorePagesLoaded(true);
      return;
    }

    fetchActivity(1)
      .then(() => {
        if (isRestoring) setRestorePagesLoaded(true);
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    eventTypesReady,
    token,
    usersFilter.selectionPayload,
    usersFilter.singleUserMode,
    eventTypeSelection,
    timeWindow,
    effectiveTzMode,
  ]);

  // Scrolls back to where the user left off, once a restore has actually
  // landed. Anchored to the row itself, not a pixel offset, so a slight
  // shift in the re-fetched list doesn't throw it off.
  const pendingTopActivityIdRef = useRef<string | null>(
    restoreState?.topActivityId ?? null
  );
  useEffect(() => {
    if (!restorePagesLoaded || !pendingTopActivityIdRef.current) return;
    const targetId = pendingTopActivityIdRef.current;
    pendingTopActivityIdRef.current = null;

    // Drawer's still animating open on the first restore, so the row's
    // there but its layout may not be settled yet — retry a few frames.
    let cancelled = false;
    let attemptsLeft = 5;
    const tryScroll = () => {
      if (cancelled) return;
      const row = bodyRef.current?.querySelector<HTMLElement>(
        `[data-activity-id="${targetId}"]`
      );
      if (row) {
        row.scrollIntoView({ block: 'start' });
        return;
      }
      if (--attemptsLeft > 0) requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
    };
  }, [restorePagesLoaded]);

  const dayGroups = useMemo(() => groupByDay(activities), [activities]);

  // Re-armed whenever a page actually lands, so a single scroll dwell near
  // the bottom doesn't fire load-more repeatedly before the next page arrives.
  const loadMoreTriggeredRef = useRef(false);
  useEffect(() => {
    loadMoreTriggeredRef.current = false;
  }, [activities]);

  const handleActivityBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreActivities || loadMoreTriggeredRef.current) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      loadMoreTriggeredRef.current = true;
      fetchActivity(activitiesPage + 1).catch(console.error);
    }
  };

  // First row not yet fully scrolled past the top of the visible area —
  // used to snapshot scroll position before navigating to a shipment.
  const findTopActivityId = (): string | null => {
    const container = bodyRef.current;
    if (!container) return null;
    const containerTop = container.getBoundingClientRect().top;
    const rows = container.querySelectorAll<HTMLElement>('[data-activity-id]');
    for (const row of rows) {
      if (row.getBoundingClientRect().bottom > containerTop) {
        return row.getAttribute('data-activity-id');
      }
    }
    return null;
  };

  // --- filter chip labels ---

  const eventsLabel = eventsChipLabel(
    selectedEventTypeIds.size,
    allLeafEventTypeIds.length
  );
  const eventsActive = selectedEventTypeIds.size !== allLeafEventTypeIds.length;
  const windowLabel = timeWindowChipLabel(timeWindow);
  const windowActive = timeWindow.preset !== '7d';

  // --- CSV export ---

  // PRD: loading state while generating, repeated clicks don't double-export,
  // a real failure shows an error instead of a partial file.
  const [isCSVDownloading, setIsCSVDownloading] = useState(false);

  const handleDownload = async () => {
    if (isCSVDownloading) return;
    setIsCSVDownloading(true);

    const payload = {
      timezone: effectiveTzMode,
      single_mode: usersFilter.singleUserMode,
      users: usersFilter.selectionPayload,
      event_type: eventTypeSelection,
      time_window: timeWindow,
    };

    try {
      const res = await ExportUserActivity(token, payload);
      // 202 response is a blob, so parse its JSON message.
      if (res.status === 202) {
        let emailMessage = "We'll email you a download link when it's ready.";
        try {
          const parsed = JSON.parse(await res.data.text());
          emailMessage = parsed?.data?.message || emailMessage;
        } catch {
          // Keep the fallback above if the body isn't parseable JSON.
        }
        message.success(emailMessage);
      } else {
        saveBlobAsFile(res, 'activity-export.csv');
      }
    } catch {
      message.error('Export failed. Please try again.');
    } finally {
      setIsCSVDownloading(false);
    }
  };

  return (
    <CommonDrawer
      open={open}
      onClose={onClose}
      width={444}
      showHeader={false}
      className='activity-feed-drawer'
      focusTrap={focusTrap}
    >
      <div className='activity-feed'>
        <ActivityPanelHeader
          tzMode={effectiveTzMode}
          onTzModeChange={setTzMode}
          subjectLabel={
            usersFilter.singleUserMode && usersFilter.subjectUser
              ? `${usersFilter.subjectUser.name}'s time zone${
                  usersFilter.subjectUser.timezone
                    ? ` (${usersFilter.subjectUser.timezone.replace(/_/g, ' ')})`
                    : ''
                }`
              : undefined
          }
          isDownloading={isCSVDownloading}
          onDownload={handleDownload}
          onClose={onClose}
        />

        <div className='activity-feed-chips-row'>
          <FilterChip
            label={usersFilter.usersLabel}
            isActive={usersFilter.usersActive}
            renderOverlay={() => (
              <UsersFilterDropdown
                users={usersFilter.fetchedUsers}
                isUserSelected={usersFilter.isUserSelected}
                onToggleUser={usersFilter.onToggleUser}
                allSelected={usersFilter.allSelected}
                onToggleAll={usersFilter.onToggleAll}
                search={usersFilter.search}
                onSearchChange={usersFilter.onSearchChange}
                hasMore={usersFilter.hasMore}
                onPopupScroll={usersFilter.handlePopupScroll}
              />
            )}
          />
          <FilterChip
            label={eventsLabel}
            isActive={eventsActive}
            renderOverlay={() => (
              <EventsFilterDropdown
                groups={eventTypeGroups}
                selectedTypeIds={selectedEventTypeIds}
                onChange={handleEventTypesChange}
                onSearchChange={handleEventsSearchChange}
              />
            )}
          />
          <FilterChip
            label={windowLabel}
            isActive={windowActive}
            renderOverlay={(close) => (
              <TimeWindowFilterDropdown
                value={timeWindow}
                onChange={setTimeWindow}
                onClose={close}
              />
            )}
          />
        </div>

        <div
          className='activity-feed-body'
          ref={bodyRef}
          onScroll={handleActivityBodyScroll}
        >
          <ActivityTimeline
            groups={dayGroups}
            showActor={!usersFilter.singleUserMode}
            onActorClick={usersFilter.selectOnlyUser}
            onEventClick={(event) => {
              setActivityFeedCache({
                activities,
                activitiesPage,
                hasMoreActivities,
              });
              const snapshot: ActivityFeedRestoreState = {
                selection: usersFilter.selectionPayload,
                singleUser: usersFilter.singleUserMode
                  ? {
                      id: usersFilter.subjectUser?.id ?? '',
                      name: usersFilter.subjectUser?.name ?? '',
                    }
                  : null,
                selectedEventTypeIds: [...selectedEventTypeIds],
                timeWindow,
                tzMode,
                topActivityId: findTopActivityId(),
              };
              onEventClick?.(event, snapshot);
            }}
            emptyStateLabel={`No activity matches these filters (${windowLabel}).`}
            hasMore={hasMoreActivities}
            loading={activitiesLoading}
          />
        </div>
      </div>
    </CommonDrawer>
  );
};

export default ActivityFeedPanel;
